import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { FileArrowUp, SpinnerGap, Leaf, CheckCircle, Warning, ArrowLeft, Trash, Flask, Sparkle } from '@phosphor-icons/react';
import { API_URL } from '../config/api';
import { SEO } from '../components/SEO';

/**
 * SoilTestPage — upload a Waypoint/Logan Labs/UMass PDF, get structured values
 * + a plain-English interpretation back. Pro-grade feature for the lawn nerd segment.
 */
const SoilTestPage = () => {
  const navigate = useNavigate();
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const [history, setHistory] = useState([]);
  const [error, setError] = useState('');

  const loadHistory = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/soil-test/history`, { withCredentials: true });
      setHistory(res.data.tests || []);
    } catch {
      /* ignore */
    }
  };

  useEffect(() => { loadHistory(); }, []);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.type !== 'application/pdf') {
      toast.error('Please upload a PDF file');
      return;
    }
    if (file.size > 8 * 1024 * 1024) {
      toast.error('PDF must be under 8 MB');
      return;
    }

    setUploading(true);
    setError('');
    setResult(null);

    try {
      const base64 = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (ev) => resolve(ev.target.result.split(',')[1]);
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });

      const res = await axios.post(
        `${API_URL}/api/soil-test/parse`,
        { pdf_base64: base64 },
        { withCredentials: true, timeout: 90_000 },
      );

      if (res.data?.parsed?.error) {
        setError(res.data.parsed.error);
        toast.error(res.data.parsed.error);
      } else {
        setResult(res.data);
        toast.success('Soil test parsed');
        await loadHistory();
      }
    } catch (err) {
      const msg = err.response?.data?.detail || 'Upload failed — try a clearer PDF or retype values manually.';
      setError(msg);
      toast.error(msg);
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this soil test?')) return;
    try {
      await axios.delete(`${API_URL}/api/soil-test/${id}`, { withCredentials: true });
      await loadHistory();
      if (result?.id === id) setResult(null);
      toast.success('Deleted');
    } catch {
      toast.error('Delete failed');
    }
  };

  return (
    <div className="min-h-screen bg-background" data-testid="soil-test-page">
      <SEO title="Soil Test Parser — TurfPulse" description="Upload a Waypoint, Logan Labs, or UMass soil-test PDF and get instant agronomic interpretation." />

      {/* Header */}
      <header className="border-b border-border/60 bg-card/80 backdrop-blur-sm sticky top-0 z-30">
        <div className="max-w-4xl mx-auto flex items-center justify-between px-4 py-3">
          <Button variant="ghost" size="sm" onClick={() => navigate('/app')} data-testid="soil-back-btn">
            <ArrowLeft size={16} className="mr-1.5" /> Dashboard
          </Button>
          <div className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-primary">
            <Sparkle weight="fill" size={12} /> Pro-grade
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-10 space-y-8">
        <div className="space-y-2">
          <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-primary/10 text-primary text-xs font-medium">
            <Flask size={12} weight="fill" /> Soil Test Reader
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold tracking-tight">
            Decode your soil test.
          </h1>
          <p className="text-base text-muted-foreground max-w-2xl">
            Upload the PDF from Waypoint Analytical, Logan Labs, or UMass. We extract every value and Moe tells you what to actually do about it — in plain English, no agronomy degree required.
          </p>
        </div>

        {/* Uploader */}
        <Card className="border-2 border-dashed border-border hover:border-primary/40 transition-colors">
          <CardContent className="p-8">
            <label className="cursor-pointer flex flex-col items-center gap-3 text-center">
              <div className="w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center">
                {uploading ? (
                  <SpinnerGap size={26} weight="bold" className="text-primary animate-spin" />
                ) : (
                  <FileArrowUp size={26} weight="bold" className="text-primary" />
                )}
              </div>
              <div>
                <p className="font-semibold">{uploading ? 'Parsing your soil test…' : 'Drop your soil-test PDF here'}</p>
                <p className="text-xs text-muted-foreground mt-1">Waypoint · Logan Labs · UMass · A&amp;L · any text-based PDF — 8 MB max</p>
              </div>
              <input
                ref={fileRef}
                type="file"
                accept="application/pdf"
                className="hidden"
                onChange={handleUpload}
                disabled={uploading}
                data-testid="soil-pdf-input"
              />
              <Button disabled={uploading} type="button" onClick={() => fileRef.current?.click()} data-testid="soil-upload-btn">
                {uploading ? 'Parsing…' : 'Choose PDF'}
              </Button>
            </label>
            {error && (
              <div className="mt-4 flex items-start gap-2 rounded-md bg-destructive/10 border border-destructive/30 p-3 text-xs text-destructive" data-testid="soil-error">
                <Warning size={14} className="shrink-0 mt-0.5" />
                <span>{error}</span>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Result */}
        {result && <SoilTestResult test={result} />}

        {/* History */}
        {history.length > 0 && (
          <div className="space-y-3" data-testid="soil-history">
            <h2 className="text-base font-semibold">Past tests</h2>
            <div className="space-y-2">
              {history.map((t) => {
                const p = t.parsed || {};
                return (
                  <Card key={t.id} className={`border ${result?.id === t.id ? 'border-primary/60' : 'border-border/60'}`}>
                    <CardContent className="p-3 flex items-center justify-between gap-3">
                      <button
                        className="flex-1 text-left"
                        onClick={() => setResult(t)}
                        data-testid={`soil-history-${t.id}`}
                      >
                        <div className="text-sm font-medium truncate">{p.interpretation?.headline || 'Soil test'}</div>
                        <div className="text-[11px] text-muted-foreground">
                          {p.lab || 'Unknown lab'} · pH {p.ph ?? '—'} · CEC {p.cec ?? '—'} · {new Date(t.created_at).toLocaleDateString()}
                        </div>
                      </button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 w-7 p-0 text-muted-foreground hover:text-destructive"
                        onClick={() => handleDelete(t.id)}
                        data-testid={`soil-delete-${t.id}`}
                      >
                        <Trash size={14} />
                      </Button>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

const SoilTestResult = ({ test }) => {
  const p = test.parsed || {};
  const interp = p.interpretation || {};
  const mn = p.macronutrients || {};
  const bs = p.base_saturation_pct || {};
  const micro = p.micronutrients || {};

  if (p.error) {
    return (
      <Card className="border border-destructive/50 bg-destructive/5">
        <CardContent className="p-5 text-sm text-destructive flex items-center gap-2">
          <Warning size={18} /> {p.error}
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4" data-testid="soil-result">
      <Card className="border-2 border-primary/40 bg-primary/5">
        <CardContent className="p-5 sm:p-7 space-y-4">
          <div className="flex items-start gap-2">
            <Leaf weight="fill" size={18} className="text-primary shrink-0 mt-0.5" />
            <div>
              <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Moe's read</p>
              <h2 className="text-lg sm:text-xl font-bold" data-testid="soil-headline">{interp.headline || 'Interpreted'}</h2>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
            {interp.ph_read && <InfoBlock label="pH read" value={interp.ph_read} />}
            {interp.cec_read && <InfoBlock label="CEC read" value={interp.cec_read} />}
          </div>
          {interp.nutrient_call_outs?.length > 0 && (
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground mb-2">Call-outs</p>
              <ul className="space-y-1.5">
                {interp.nutrient_call_outs.map((n, i) => (
                  <li key={i} className="flex gap-2 text-sm">
                    <CheckCircle size={16} weight="fill" className="shrink-0 mt-0.5 text-primary" />
                    <span>{n}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {interp.next_actions?.length > 0 && (
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground mb-2">Next actions</p>
              <ul className="space-y-1.5">
                {interp.next_actions.map((a, i) => (
                  <li key={i} className="flex gap-2 text-sm">
                    <span className="text-primary font-bold">›</span>
                    <span>{a}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Raw values */}
      <Card>
        <CardContent className="p-5 space-y-4">
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Lab</p>
            <p className="text-sm font-medium">{p.lab || 'Unknown lab'} {p.sample_date ? `· ${p.sample_date}` : ''}{p.sample_location ? ` · ${p.sample_location}` : ''}</p>
          </div>

          <div className="grid grid-cols-3 md:grid-cols-6 gap-2">
            <Stat label="pH" value={p.ph} />
            <Stat label="Buffer pH" value={p.buffer_ph} />
            <Stat label="OM %" value={p.om_pct} />
            <Stat label="CEC" value={p.cec} />
            <Stat label="P ppm" value={mn.p_ppm} />
            <Stat label="K ppm" value={mn.k_ppm} />
            <Stat label="Ca ppm" value={mn.ca_ppm} />
            <Stat label="Mg ppm" value={mn.mg_ppm} />
            <Stat label="S ppm" value={mn.s_ppm} />
            <Stat label="Na ppm" value={mn.na_ppm} />
          </div>

          {Object.values(bs).some(v => v != null) && (
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground mb-2">Base saturation %</p>
              <div className="grid grid-cols-5 gap-2">
                <Stat label="K" value={bs.k} suffix="%" />
                <Stat label="Mg" value={bs.mg} suffix="%" />
                <Stat label="Ca" value={bs.ca} suffix="%" />
                <Stat label="Na" value={bs.na} suffix="%" />
                <Stat label="H" value={bs.h} suffix="%" />
              </div>
            </div>
          )}

          {Object.values(micro).some(v => v != null) && (
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground mb-2">Micronutrients (ppm)</p>
              <div className="grid grid-cols-5 gap-2">
                <Stat label="Fe" value={micro.fe} />
                <Stat label="Zn" value={micro.zn} />
                <Stat label="Mn" value={micro.mn} />
                <Stat label="Cu" value={micro.cu} />
                <Stat label="B" value={micro.b} />
              </div>
            </div>
          )}

          {p.units_note && (
            <p className="text-[11px] text-muted-foreground italic">{p.units_note}</p>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

const Stat = ({ label, value, suffix = '' }) => (
  <div className="rounded border border-border/50 bg-card px-2 py-1.5 text-center">
    <div className="text-[9px] uppercase tracking-wider text-muted-foreground">{label}</div>
    <div className="text-sm font-mono font-semibold">{value != null ? `${value}${suffix}` : '—'}</div>
  </div>
);

const InfoBlock = ({ label, value }) => (
  <div className="rounded-md border border-border/40 bg-background/60 p-3">
    <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
    <div className="text-sm mt-0.5">{value}</div>
  </div>
);

export default SoilTestPage;
